/**
 * ⚡ FINAL Optimized FacetFiltersForm + Sticky Facets
 * Android / iOS / Desktop SAFE
 * INP, CLS, LCP optimized
 */

/* ===============================  
   🔁 DEBOUNCE UTILITY
================================= */
function debounce(fn, delay) {
  let timer;
  return function () {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, arguments), delay);
  };
}

/* =======================================================
   🛍️ FACET FILTERS — INSTANT RESPONSE (NO IDLE)
========================================================== */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce((e) => this.onSubmitHandler(e), 300);

    const form = this.querySelector("form");
    if (form) {
      form.addEventListener("input", this.debouncedOnSubmit);
      form.addEventListener("change", this.debouncedOnSubmit); // iOS safe
    }
  }

  static setListeners() {
    window.addEventListener("popstate", (event) => {
      const searchParams =
        event.state?.searchParams ?? FacetFiltersForm.searchParamsInitial;

      if (searchParams !== FacetFiltersForm.searchParamsPrev) {
        FacetFiltersForm.renderPage(searchParams, null, false);
      }
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const grid = document.getElementById("ProductGridContainer");
    grid?.classList.add("loading");

    document
      .querySelectorAll(".loading__spinner")
      .forEach((s) => s.classList.remove("hidden"));

    FacetFiltersForm.getSections().forEach((section) => {
      const url = `${location.pathname}?section_id=${section.section}&${searchParams}`;
      const cached = FacetFiltersForm.filterData.find((d) => d.url === url);

      cached
        ? FacetFiltersForm.renderFromCache(cached.html)
        : FacetFiltersForm.fetchAndRender(url);
    });

    if (updateURLHash) {
      history.pushState(
        { searchParams },
        "",
        `${location.pathname}${searchParams ? "?" + searchParams : ""}`
      );

    }
  }

  static fetchAndRender(url) {
    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        FacetFiltersForm.filterData.push({ url, html });
        FacetFiltersForm.renderFromCache(html);
      })
      .catch(console.error);
  }

  static renderFromCache(html) {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    FacetFiltersForm.updateDOM(parsed);
  }

  static updateDOM(parsedHTML) {
    // ✅ existing code (grid + facets replace)
    const newGrid = parsedHTML.getElementById("ProductGridContainer");
    const oldGrid = document.getElementById("ProductGridContainer");
    if (newGrid && oldGrid) oldGrid.innerHTML = newGrid.innerHTML;

    // The swap above drops in a brand-new infinite-scroll sentinel (for the
    // sorted/filtered result set) that has no observer attached yet.
    window.initInfiniteScrollSentinels?.();

    [
      ".active-facets-desktop",
      ".active-facets-mobile",
      "#ProductCount",
      "#ProductCountDesktop"
    ].forEach((sel) => {
      const newEl = parsedHTML.querySelector(sel);
      const curEl = document.querySelector(sel);
      if (newEl && curEl) curEl.innerHTML = newEl.innerHTML;
    });

    document
      .querySelectorAll(".loading")
      .forEach((el) => el.classList.remove("loading"));

    document
      .querySelectorAll(".loading__spinner")
      .forEach((el) => el.classList.add("hidden"));

    // 🔁 Re-upgrade custom elements
    const fff = document.querySelector("facet-filters-form");
    if (fff) customElements.upgrade(fff);

    document.querySelectorAll("price-range").forEach((el) => customElements.upgrade(el));
    document.querySelectorAll("facet-remove").forEach((el) => customElements.upgrade(el));

    /* ================================
       🔥 FIX: FACET DRAWER RESET
    ================================= */
    requestAnimationFrame(() => {
      // body scroll unlock
      document.body.classList.remove("overflow-hidden");

      // close all drawers
      document
        .querySelectorAll(".facets__disclosure, .facets__wrapper details")
        .forEach((details) => {
          details.removeAttribute("open");
        });

      // reset aria
      document.querySelectorAll("[aria-expanded]").forEach((el) => {
        el.setAttribute("aria-expanded", "false");
      });

      // re-bind toggles
      document.querySelectorAll(".facets__summary").forEach((summary) => {
        summary.onclick = null;
        summary.addEventListener("click", (e) => {
          e.preventDefault();

          const details = summary.closest("details");
          if (!details) return;

          const isOpen = details.hasAttribute("open");

          document
            .querySelectorAll(".facets__wrapper details[open]")
            .forEach((d) => d !== details && d.removeAttribute("open"));

          if (isOpen) {
            details.removeAttribute("open");
            summary.setAttribute("aria-expanded", "false");
          } else {
            details.setAttribute("open", "");
            summary.setAttribute("aria-expanded", "true");
          }
        });
      });
    });
  }



  createSearchParams(form) {
    return new URLSearchParams(new FormData(form)).toString();
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const forms = Array.from(document.querySelectorAll("facet-filters-form form"));

    // Desktop/mobile/drawer each render their own copy of the same filter
    // fields (same `name`, different form). Merging them in DOM order lets
    // a stale duplicate (e.g. a mobile sort <select> still on "manual")
    // overwrite the value the user just changed. Process the form that
    // was actually interacted with last so its values always win.
    const activeForm = event.target?.closest?.("form") || null;
    if (activeForm) {
      const activeIndex = forms.indexOf(activeForm);
      if (activeIndex > -1) {
        forms.splice(activeIndex, 1);
        forms.push(activeForm);
      }
    }

    const merged = new URLSearchParams();

    forms.forEach((form) => {
      const formData = new FormData(form);
      for (const [key, value] of formData.entries()) {
        if (value === "") continue;
        merged.set(key, value);
      }
    });

    FacetFiltersForm.renderPage(merged.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    const href = event.currentTarget.href;
    const params = href.includes("?") ? href.split("?")[1] : "";

    // RESET forms
    document.querySelectorAll("facet-filters-form form").forEach((form) => {
      form.reset();
    });

    // RESET price range
    document.querySelectorAll("price-range").forEach((range) => {
      const minInput = range.querySelector(".price-input-min");
      const maxInput = range.querySelector(".price-input-max");
      const rMin = range.querySelector(".range-min");
      const rMax = range.querySelector(".range-max");

      if (!minInput || !maxInput || !rMin || !rMax) return;

      minInput.value = "";
      maxInput.value = "";
      rMin.value = rMin.min;
      rMax.value = rMax.max;

      range.updateVisuals?.();
    });

    FacetFiltersForm.renderPage(params);
  }




  static getSections() {
    const sections = [];

    // ✅ product grid section id
    const gridSection = document.getElementById("product-grid")?.dataset.id;
    if (gridSection) sections.push({ section: gridSection });

    // ✅ filters section id (common Dawn ids + fallback)
    const possibleFiltersSection =
      document.querySelector("#main-collection-filters")?.dataset.id ||
      document.querySelector("[data-facets-section-id]")?.getAttribute("data-facets-section-id") ||
      document.querySelector(".facets-container")?.closest("[data-section-id]")?.getAttribute("data-section-id") ||
      document.querySelector("facet-filters-form")?.closest("[data-section-id]")?.getAttribute("data-section-id");

    if (possibleFiltersSection && possibleFiltersSection !== gridSection) {
      sections.push({ section: possibleFiltersSection });
    }

    return sections;
  }

}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

/* =======================================================
   PRICE RANGE — FAST & SAFE
========================================================== */
class PriceRange extends HTMLElement {
  constructor() {
    super();


    this.closest("details")?.addEventListener("click", (e) => {
      e.stopPropagation();
    });

    this.textMin = this.querySelector('.price-input-min');
    this.textMax = this.querySelector('.price-input-max');
    this.rangeMin = this.querySelector('.range-min');
    this.rangeMax = this.querySelector('.range-max');
    this.minPriceText = this.querySelector('.min-range-price');
    this.maxPriceText = this.querySelector('.max-range-price');
    this.track = this.querySelector('.price-slider-connect');

    if (!this.rangeMin || !this.rangeMax || !this.textMin || !this.textMax) return;

    this.rangeMin.addEventListener('input', () => this.sliderChanged('min'));
    this.rangeMax.addEventListener('input', () => this.sliderChanged('max'));
    this.textMin.addEventListener('input', () => this.textChanged());
    this.textMax.addEventListener('input', () => this.textChanged());

    this.syncFromInput();
    this.updateVisuals();
  }

  sliderChanged(which) {
    let min = +this.rangeMin.value;
    let max = +this.rangeMax.value;

    if (max - min < 100) {
      which === 'min'
        ? (this.rangeMin.value = max - 100)
        : (this.rangeMax.value = min + 100);
    }

    this.textMin.value = this.rangeMin.value;
    this.textMax.value = this.rangeMax.value;

    this.updateVisuals();
    this.submitFilter();
  }

  textChanged() {
    let min = parseInt(this.textMin.value || "0");
    let max = parseInt(this.textMax.value || this.rangeMax.max);
    if (max - min < 100) max = min + 100;

    this.rangeMin.value = min;
    this.rangeMax.value = max;

    this.updateVisuals();
    this.submitFilter();
  }

  syncFromInput() {
    this.rangeMin.value = parseInt(this.textMin.value || "0");
    this.rangeMax.value = parseInt(this.textMax.value || this.rangeMax.max);
  }

  updateVisuals() {
    const min = +this.rangeMin.value;
    const max = +this.rangeMax.value;
    const maxRange = +this.rangeMax.max;

    const p1 = (min / maxRange) * 100;
    const p2 = (max / maxRange) * 100;

    this.style.setProperty('--range-min-percent', p1 + '%');
    this.style.setProperty('--range-max-percent', p2 + '%');

    if (this.track) {
      this.track.style.left = p1 + '%';
      this.track.style.width = (p2 - p1) + '%';
    }

    this.minPriceText.textContent = `₹ ${min}`;
    this.maxPriceText.textContent = `₹ ${max}`;
  }

  submitFilter() {
    this.textMin.dispatchEvent(new Event("change", { bubbles: true }));
    this.textMax.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

customElements.define("price-range", PriceRange);

/* =======================================================
   FACET REMOVE
========================================================== */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const link = this.querySelector("a");
    if (!link) return;

    link.setAttribute("role", "button");

    const handleClick = (e) => {
      e.preventDefault();
      const form =
        this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
      form?.onActiveFilterClick?.(e);
    };

    link.addEventListener("click", handleClick);
    link.addEventListener("keyup", (e) => {
      if (e.code === "Space") handleClick(e);
    });
  }
}
customElements.define("facet-remove", FacetRemove);

/* =======================================================
   STICKY FACETS (desktop) — handled entirely by native CSS
   position: sticky now (see .facets-wrapper in
   component-facets.css). That replaced a manual rAF-throttled
   scroll handler that computed and set `top` via inline styles;
   it used the wrong header class to detect "header actually
   hidden" (always treated it as visible), and its own show/hide
   trigger wasn't synced with the header's separate scroll-direction
   -aware reveal logic — scrolling back up could reveal the header
   while the facets bar stayed stuck in its "removed" state.
   Native sticky positioning doesn't need any of that: it shows/hides
   in lockstep with scroll direction automatically.
========================================================== */

/* =======================================================
   🔥 FIX: REMOVE ALL / ACTIVE FACET LINKS (GLOBAL)
========================================================== */
document.addEventListener("click", (e) => {
  const link = e.target.closest(
    ".active-facets__clear a, .active-facets a[href*='filter']"
  );
  if (!link) return;

  e.preventDefault();

  const facetForm = document.querySelector("facet-filters-form");

  if (!facetForm || !facetForm.onActiveFilterClick) {
    window.location.href = link.href;
    return;
  }

  facetForm.onActiveFilterClick({
    preventDefault() { },
    currentTarget: link
  });
});
